import { useState } from 'react';
import HubLayout from '../../../components/hub/HubLayout.jsx';
import config from '../../../data/hubs/comptia-secplus/config.js';

const DOMAINS = [
  {
    id: 'D1', title: 'General Security Concepts', weight: '12%', open: true,
    intro: 'Foundational vocabulary and frameworks. Light on technique, heavy on definitions — easy points if memorized cold.',
    sections: [
      { h: 'Core Concepts', items: [
        ['Security controls', '— categories (technical, managerial, operational, physical) and types (preventive, deterrent, detective, corrective, compensating, directive).'],
        ['CIA triad', '— Confidentiality, Integrity, Availability. Plus authenticity, non-repudiation.'],
        ['Zero Trust', '— control plane (policy engine, policy administrator), data plane (subject, system, policy enforcement point).'],
        ['Physical security', '— bollards, mantraps/access control vestibules, fencing, video surveillance, sensors, lighting.'],
        ['Deception & disruption', '— honeypots, honeynets, honeytokens, honeyfiles.'],
      ]},
      { h: 'Change Management & Cryptography', items: [
        ['Change management', '— approval process, ownership, stakeholders, impact analysis, test results, backout plans, maintenance windows, SOPs.'],
        ['PKI', '— public/private keys, root of trust, CA, intermediate CA, CSR, OCSP vs. CRL, certificate pinning.'],
        ['Encryption', '— symmetric (AES) vs. asymmetric (RSA, ECC), key length, key exchange (DH, ECDH).'],
        ['Hashing & integrity', '— SHA-2/SHA-3, salting, key stretching (PBKDF2, bcrypt, scrypt, Argon2), HMAC, digital signatures.'],
        ['Steganography, tokenization, data masking, blockchain.', ''],
      ]},
    ],
  },
  {
    id: 'D2', title: 'Threats, Vulnerabilities, and Mitigations', weight: '22%',
    intro: 'The largest single domain. Memorize attack types and the mitigation that pairs with each — this is where most exam questions live.',
    sections: [
      { h: 'Threat Actors & Vectors', items: [
        ['Actors', '— nation-state, organized crime, hacktivist, insider threat, unskilled attacker, shadow IT. Attributes: internal vs. external, resources, sophistication, motivation.'],
        ['Vectors', '— message-based (email, SMS, IM), image-based, file-based, voice call, removable device, vulnerable software, unsecured networks, BYOD, supply chain.'],
        ['Social engineering', '— phishing, vishing, smishing, BEC, whaling, pretexting, watering hole, brand impersonation, typosquatting, pharming.'],
      ]},
      { h: 'Vulnerability & Attack Types', items: [
        ['Application', '— buffer overflow, race condition (TOCTOU), memory injection, malicious update, SQL injection, XSS, directory traversal.'],
        ['Hardware/firmware', '— EOL/legacy, end-of-support, side-channel, fault injection.'],
        ['Cryptographic', '— weak cipher, downgrade, birthday attack, collision.'],
        ['Network', '— DDoS, on-path (MITM), DNS attacks, ARP poisoning, wireless (deauth, RF jamming), credential replay, malicious code.'],
        ['Malware', '— ransomware, trojan, worm, spyware, bloatware, virus, keylogger, logic bomb, rootkit.'],
      ]},
      { h: 'Mitigation Techniques', items: [
        ['Segmentation, ACLs, isolation, patching, encryption, monitoring, least privilege, configuration enforcement, decommissioning, hardening.', ''],
        ['Hardening targets', '— endpoints, servers, network appliances, mobile, cloud, IoT/embedded, ICS/SCADA, RTOS.'],
      ]},
    ],
  },
  {
    id: 'D3', title: 'Security Architecture', weight: '18%',
    intro: 'How systems are designed, deployed, and protected at the architectural level. Includes cloud, network design, and data protection.',
    sections: [
      { h: 'Architecture Models', items: [
        ['Cloud', '— responsibility matrix, hybrid, third-party vendors, IaaS/PaaS/SaaS, serverless, microservices.'],
        ['Network infrastructure', '— physical isolation/airgap, logical segmentation, SDN, on-premises vs. cloud.'],
        ['Centralized vs. decentralized, virtualization, containerization, IoT, ICS/SCADA, embedded, RTOS.', ''],
      ]},
      { h: 'Secure Network Design', items: [
        ['Devices', '— firewall (stateful, NGFW, WAF), IDS/IPS, load balancer, proxy, jump server, sensors.'],
        ['Secure communication', '— VPN (IPsec, TLS), SD-WAN, SASE, port security (802.1X, EAP).'],
        ['Selection criteria', '— failure modes (fail-open vs. fail-closed), device attribute (active/passive, inline vs. tap/monitor).'],
      ]},
      { h: 'Data Protection & Resilience', items: [
        ['Data classification', '— sensitive, confidential, public, restricted, private, critical.'],
        ['Data states', '— at rest, in transit, in use. Sovereignty & geolocation.'],
        ['Resilience', '— high availability, load balancing vs. clustering, site considerations (hot/warm/cold), platform diversity.'],
        ['Backups', '— frequency, encryption, snapshots, recovery, replication, journaling.'],
        ['Power', '— generators, UPS.'],
      ]},
    ],
  },
  {
    id: 'D4', title: 'Security Operations', weight: '28%',
    intro: 'The largest domain by weight. Day-to-day ops: monitoring, IR, hardening, identity, automation. Heavy on PBQ-friendly content.',
    sections: [
      { h: 'Identity & Access Management', items: [
        ['Provisioning/deprovisioning, identity proofing, federation, SSO (LDAP, OAuth, SAML), interoperability, attestation.', ''],
        ['Access controls', '— MAC, DAC, RBAC, rule-based, attribute-based (ABAC), time-of-day, least privilege.'],
        ['MFA', '— something you know/have/are/do/where. Hard/soft authenticators, biometrics, TOTP/HOTP.'],
        ['Password best practice', '— length, complexity, reuse, expiration, age, password managers, passwordless.'],
        ['Privileged access management (PAM)', '— JIT, vaulting, ephemeral credentials.'],
      ]},
      { h: 'Monitoring, Logging & Hardening', items: [
        ['Hardening', '— mobile device (MDM, BYOD/COPE/CYOD), workstation, server, network, cloud, install/patch/configure.'],
        ['Asset management', '— acquisition, assignment, monitoring, tracking, disposal/decommissioning, sanitization, certification.'],
        ['Vulnerability management', '— scans, static/dynamic analysis, CVSS, CVE, prioritization, patching, compensating controls.'],
        ['Monitoring', '— SIEM, SOAR, log aggregation, alerts, archiving, NetFlow.'],
      ]},
      { h: 'Incident Response & Investigations', items: [
        ['IR process', '— preparation, identification, containment, eradication, recovery, lessons learned.'],
        ['Activities', '— training, tabletop, simulations, root cause, threat hunting, digital forensics (legal hold, chain of custody, acquisition, reporting, preservation, e-discovery).'],
        ['Automation & orchestration', '— playbooks, ticketing, escalation, scripted workflows.'],
      ]},
      { h: 'Email & Endpoint Security', items: [
        ['Email', '— DKIM, DMARC, SPF, gateway.'],
        ['Endpoint', '— EDR/XDR, antivirus, FIM, HIPS, allowlists/denylists.'],
        ['Web filtering, DLP, content filtering, URL/domain reputation.', ''],
      ]},
    ],
  },
  {
    id: 'D5', title: 'Security Program Management & Oversight', weight: '20%',
    intro: 'Governance, risk, third-party, compliance. Wordy and policy-heavy — favorite territory for "best answer" multiple-choice trickery.',
    sections: [
      { h: 'Governance', items: [
        ['Guidelines, policies (AUP, info security, BC, DR, IR, SDLC, change management).', ''],
        ['Standards', '— password, access control, physical, encryption.'],
        ['Procedures & external considerations', '— regulatory, legal, industry, local/regional/national/global.'],
        ['Roles & responsibilities', '— owners, controllers, processors, custodians/stewards.'],
      ]},
      { h: 'Risk Management', items: [
        ['Identification, assessment', '— ad hoc, recurring, one-time, continuous.'],
        ['Quantitative', '— SLE = AV × EF; ALE = SLE × ARO. Qualitative — H/M/L matrix.'],
        ['Risk register', '— KRIs, owners, threshold.'],
        ['Treatment', '— transfer, accept (with exemption/exception), avoid, mitigate.'],
        ['BIA', '— RTO, RPO, MTTR, MTBF.'],
      ]},
      { h: 'Third-Party & Compliance', items: [
        ['Vendor assessment', '— penetration testing, right-to-audit, supply chain, independent assessments, questionnaires.'],
        ['Agreements', '— SLA, MOA, MOU, MSA, WO/SOW, NDA, BPA.'],
        ['Compliance reporting', '— internal/external, due diligence, attestation, acknowledgement.'],
        ['Privacy', '— legal implications, data subject, controller vs. processor, ownership, retention, right to be forgotten.'],
      ]},
      { h: 'Audits, Assessments & Awareness', items: [
        ['Audits', '— internal vs. external, regulatory, attestation, examinations.'],
        ['Penetration testing', '— known/partially known/unknown environment, reconnaissance (passive vs. active).'],
        ['Security awareness', '— phishing campaigns, recognizing anomalous behavior, user guidance, reporting/monitoring, development & execution of training.'],
      ]},
    ],
  },
];

export default function Domains() {
  const [filter, setFilter] = useState('');
  const [openMap, setOpenMap] = useState(() => {
    const m = {};
    DOMAINS.forEach((d) => { m[d.id] = !!d.open; });
    return m;
  });

  const f = filter.trim().toLowerCase();

  function expandAll() {
    const allOpen = DOMAINS.every((d) => openMap[d.id]);
    const next = {};
    DOMAINS.forEach((d) => { next[d.id] = !allOpen; });
    setOpenMap(next);
  }

  function matches(d) {
    if (!f) return true;
    if (d.title.toLowerCase().includes(f) || d.id.toLowerCase().includes(f)) return true;
    return d.sections.some((s) =>
      s.h.toLowerCase().includes(f) ||
      s.items.some(([k, v]) => (k + ' ' + v).toLowerCase().includes(f))
    );
  }

  const visible = DOMAINS.filter(matches);

  return (
    <HubLayout config={config}>
      <section>
        <div className="section-head">
          <h2>The Five Domains</h2>
          <span className="section-tag">{config.code}</span>
        </div>
        <p>Click any domain to expand the breakdown. Use search to filter across objectives. Weighting reflects the percentage of exam questions drawn from each area.</p>

        <div className="toolbar">
          <div className="search">
            <input
              type="text"
              placeholder="Filter objectives, terms, acronyms..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <button type="button" className="btn-mini" onClick={expandAll}>Expand all</button>
        </div>

        <div className="domain-list">
          {visible.map((d) => (
            <details
              key={d.id}
              className="domain"
              open={!!openMap[d.id]}
              onToggle={(e) => {
                const isOpen = e.currentTarget.open;
                setOpenMap((m) => (m[d.id] === isOpen ? m : { ...m, [d.id]: isOpen }));
              }}
            >
              <summary>
                <span className="domain-id">{d.id}</span>
                <span className="domain-title">{d.title}</span>
                <span className="summary-meta">
                  <span>{d.weight}</span>
                  <span className="weight-bar" style={{ '--w': d.weight }}></span>
                </span>
              </summary>
              <div className="details-body">
                <p>{d.intro}</p>
                {d.sections.map((s) => (
                  <div key={s.h}>
                    <h4>{s.h}</h4>
                    <ul>
                      {s.items.map(([k, v], i) => (
                        <li key={i}>
                          <strong>{k}</strong>{v ? ' ' : ''}{v}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
          ))}
          {visible.length === 0 && <p style={{ opacity: 0.6 }}>No domains match that filter.</p>}
        </div>
      </section>

      <section>
        <div className="section-head">
          <h2>Weighting Map</h2>
          <span className="section-tag">allocate study time accordingly</span>
        </div>
        <p>Spend study time proportionally. Domain 4 alone accounts for nearly a third of exam questions; under-investing here is the most common cause of a failed first attempt.</p>

        <div className="grid grid-3">
          <div className="card"><div className="card-num"><span>D1</span><span className="weight">12%</span></div><h3>General Concepts</h3><p>Definitions, control types, CIA, PKI fundamentals. ~1 study week.</p></div>
          <div className="card"><div className="card-num"><span>D2</span><span className="weight">22%</span></div><h3>Threats &amp; Mitigations</h3><p>Attacks, malware, mitigations. ~2.5 study weeks.</p></div>
          <div className="card"><div className="card-num"><span>D3</span><span className="weight">18%</span></div><h3>Architecture</h3><p>Network, cloud, data protection. ~2 study weeks.</p></div>
          <div className="card"><div className="card-num"><span>D4</span><span className="weight">28%</span></div><h3>Operations</h3><p>IAM, IR, monitoring, hardening. ~3.5 study weeks.</p></div>
          <div className="card"><div className="card-num"><span>D5</span><span className="weight">20%</span></div><h3>Program Management</h3><p>Governance, risk, compliance. ~2 study weeks.</p></div>
          <div className="card"><div className="card-num"><span>FINAL</span><span className="weight">100%</span></div><h3>Mock + Review</h3><p>Practice exams + cheat sheet. Final 1 week sprint.</p></div>
        </div>
      </section>
    </HubLayout>
  );
}
